<template>
    <div class="flex flex-col dark:text-white">
        <div class="flex flex-col my-5">
            <form class="flex items-center content-center" @submit.prevent="users = []; next_page_url = false; fetch();">
                <div class="flex my-4">
                    <litepie-datepicker
                        v-model="filters.date"
                        :formatter="{
                            date: 'DD/MM/YYYY',
                            month: 'MMM'
                        }"
                        :shortcuts="false"
                    ></litepie-datepicker>
                </div>
                <div class="grow">
                    <input v-model="filters.text" type="text" id="text" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Filtre por nome ou email">
                </div>
                <div class="flex flex-none ml-auto">
                    <button
                        @click.prevent="clearFilters"
                        :disabled="false"
                        type="button"
                        :class="{'cursor-not-allowed': loading}"
                        class="inline-flex items-center px-4 py-2 mx-2 mr-2 text-sm font-semibold leading-6 text-indigo-500 transition duration-150 ease-in-out bg-transparent border-2 border-indigo-500 rounded-md shadow hover:text-white hover:bg-indigo-500"
                    >
                        Limpar filtros
                    </button>
                    <button
                        :disabled="false"
                        type="submit"
                        :class="{'cursor-not-allowed': loading}"
                        class="inline-flex items-center px-4 py-2 mx-auto text-sm font-semibold leading-6 text-white transition duration-150 ease-in-out bg-indigo-500 rounded-md shadow hover:bg-indigo-400"
                    >
                        <svg v-if="loading" class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrar
                    </button>
                </div>
            </form>
        </div>
        <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full sm:px-6 lg:px-8">
                <div class="relative overflow-hidden not-prose bg-slate-50 rounded-xl dark:bg-slate-800/25">
                    <table class="w-full text-sm border-collapse table-auto">
                        <thead>
                        <tr>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                Usuário
                            </th>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                CPF/CNPJ
                            </th>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                Status
                            </th>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                Data da última verificação
                            </th>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                Gatilhos da verificação
                            </th>
                            <th>
                                Score
                            </th>
                            <th scope="col" class="px-6 py-4 text-sm font-medium text-left">
                                Link Externo - KYC
                            </th>
                        </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-slate-800">
                            <tr
                                v-for="user in users"
                                :key="user.id"
                            >
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    <button
                                        class="text-indigo-500 hover:underline"
                                        @click.prevent="manage(user)"
                                    >
                                        {{ user.name }} {{ user.email }}
                                    </button>
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ user.type === 'company' ? user.cnpj : user.cpf }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ statusLabel(user.status) }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    <p
                                        v-if="user.last_checked_at"
                                        class="flex flex-wrap text-sm dark:text-white"
                                    >
                                        <ClockIcon
                                            class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <time
                                            :datetime="user.last_checked_at"
                                        >{{
                                            user.last_checked_at
                                        }}</time>
                                    </p>
                                    <p v-else>
                                        Não solicitado
                                    </p>
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ translateReasons(user.reasons) }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    {{ user.score ?? '*' }}
                                </td>
                                <td class="p-3 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-200">
                                    <div class="flex items-center gap-2">
                                        <a
                                            v-if="user.last_kyc_process_external_id || user.status"
                                            :href="getExternalKycUrl(user.last_kyc_process_external_id, user.type, user.cnpj)"
                                            target="_blank"
                                            class="text-indigo-500 hover:underline"
                                        >
                                            Visualizar
                                        </a>
                                        <span v-else>-</span>
                                        <span
                                            v-if="user.idwall_status"
                                            :class="idwallStatusBadgeClass(user.idwall_status)"
                                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                        >
                                            Status IDWall: {{ idwallStatusLabel(user.idwall_status) }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="flex w-full my-5">
                        <div class="flex flex-col items-center mx-auto">
                            <span class="mb-1">
                                Exibindo {{pagination.from}} a {{pagination.to}} registros de {{pagination.total}}
                            </span>
                            <pagination
                                :pagination="pagination"
                                :offset="1"
                                @paginate="fetch"
                            ></pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
ClockIcon,
} from "@heroicons/vue/24/outline";
import VueSelect from "../../components/forms/VueSelect.vue";
import Pagination from "../../components/layouts/Pagination.vue";
import { mapWritableState, mapActions } from 'pinia';
import { Store } from '../../stores/store.js';
import HelperMixin from "../../mixins/HelperMixin.vue";
import LitepieDatepicker from 'litepie-datepicker-tw3';

export default {
    components: { VueSelect, Pagination, ClockIcon, LitepieDatepicker },
    mixins: [HelperMixin],
    data() {
        return {
            loading: false,
            next_page_url: false,
            users: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11
            },
            filters: {
                text: '',
                date: [],
            },
            aegisUrl: '',
        };
    },

    computed: {
        ...mapWritableState(Store, ['user']),
    },

    mounted() {
        this.storeProduct = false;
        this.loadConfig();
        this.fetch();
    },

    methods: {
        loadConfig() {
            axios.get('/api/kyc/config')
                .then(({ data }) => {
                    this.aegisUrl = data.aegis_url?.replace('api/v1', '') || '';
                })
                .catch(error => {
                    console.error('Error loading KYC config:', error);
                });
        },
        clearFilters() {
            this.users = [];
            this.filters.status = [];
            this.filters.text = '';
            this.filters.date = [];
            this.next_page_url = false;
            this.fetch();
        },
        translateReasons(reasons) {
            if (!reasons) {
                return null;
            }

            reasons = JSON.parse(reasons);

            let reasonTranslations = {
                'first_sell': 'Primeira venda',
                'without_valid_documents': 'Não possui documentos validos',
                'score_below_fifty': 'Pontos abaixo de 50',
                'total_revenue_amount': 'Faixa de faturamento',
            };

            return reasons.map(reason => reasonTranslations[reason] ?? reason).join(', ');
        },
        getExternalKycUrl(externalId, userType, cnpj) {
            if (userType === 'company' && cnpj) {
                return `${this.aegisUrl}company-report/${cnpj}/kyc-provider-page`;
            }

            return `${this.aegisUrl}kyc-process/${externalId}/customer-kyc-provider-page`;
        },
        manage(user) {
            this.user = user;
            this.$router.push({
                name: 'user.commissions',
                params: { user_id: user.id }
            });
        },
        statusLabel(status) {
            return {
                'checked': 'Verificado',
                'required': 'Requerido',
                'processing': 'Em processamento',
                'inconclusive': 'Inconcluso',
            }?.[status] ?? 'Não solicitado';
        },
        idwallStatusLabel(status) {
            return {
                'approved':                'Aprovado',
                'finished':                'Aprovado',
                'pending':                 'Pendente',
                'in_progress':             'Em análise',
                'waiting_manual_approval': 'Aguardando revisão manual',
                'waiting_manual_action':   'Aguardando ação manual',
                'inconclusive':            'Inconclusivo',
                'rejected':                'Rejeitado',
                'cancelled':               'Cancelado',
                'invalid':                 'Inválido',
            }?.[status] ?? status;
        },
        idwallStatusBadgeClass(status) {
            return {
                'approved':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'finished':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'pending':                 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'in_progress':             'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                'waiting_manual_approval': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'waiting_manual_action':   'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'inconclusive':            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'rejected':                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'cancelled':               'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                'invalid':                 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            }?.[status] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        },
        fetchMissingIdwallStatuses(users) {
            const missing = users.filter(u => !u.idwall_status);

            missing.forEach(user => {
                axios.get(`/api/kyc/users/${user.id}/idwall-status`)
                    .then(({ data }) => {
                        if (data.idwall_status) {
                            const index = this.users.findIndex(u => u.id === user.id);
                            if (index !== -1) {
                                this.users[index] = { ...this.users[index], idwall_status: data.idwall_status };
                                this.users = [...this.users];
                            }
                        }
                    })
                    .catch(() => {});
            });
        },
        fetch() {
            this.loading = true;

            const params = {};
            params['page'] = this.pagination.current_page;
            params['filter[text]'] = this.filters.text;
            params['filter[betweenDates]'] = this.filters.date.join();

            axios.get('/api/kyc/users', {
                params: params
            }).then(({data}) => {
                this.users = data.data;

                this.pagination = {
                    ...this.pagination,
                    ...data
                };

                this.next_page_url = data.next_page_url;
                this.loading = false;

                this.fetchMissingIdwallStatuses(this.users);
            }).catch(error => {
                this.loading = false;
                // this.$toast.error(error.response.data.message);
            });
        },
    }
}
</script>
